import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { getLogger } from '@logtape/logtape';
import { Button, Collapse, Divider, Loader, ScrollArea } from '@mantine/core';
import { ChevronDown } from 'lucide-react';
import { FC, ReactElement, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import FilterHeader from '@/components/Filtering/FilterHeader.tsx';
import { Page } from '@/components/Page.tsx';
import PageTitleBar from '@/components/PageTitleBar.tsx';
import AppointmentCard from '@/components/room-view/AppointmentCard/AppointmentCard.tsx';
import KanbanColumnComponent from '@/components/room-view/KanbanColumn.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { useCheckInActions } from '@/hooks/use-check-in-actions.ts';
import { usePendingMoves } from '@/hooks/use-pending-moves.ts';
import useAppointmentsForDate from '@/hooks/useAppointmentsForDate.ts';
import useCheckInData from '@/hooks/useCheckInData.ts';
import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerData } from '@/hooks/useEnrichedPractitionerData.ts';

import { Room } from '@/models/Room.ts';
import { ColumnType } from '@/models/RoomView.ts';
import { DailyAppointment } from '@/models/appointment/Appointment.ts';
import { CheckInDataMap } from '@/models/call-system/dtos/user-appointment-dto.ts';

import { addPatientToAppointment } from '@/services/appointmentService.ts';
import { showWarningNotification } from '@/services/notificationService.ts';

import { useFilterStore } from '@/stores/filterStore.ts';
import { WalkInModalMode, useWalkInStore } from '@/stores/walkInStore.ts';

import { getEternoPatientId, getInstanceId, getPvsPatientId } from '@/utils/appointmentUtils.ts';
import { filterAppointments, filterRoomsBySelection } from '@/utils/filter-utils.ts';
import { openAppointmentScheduler } from '@/utils/modal/appointmentSchedulerUtils.tsx';
import { openPatientModal, openWalkInModal } from '@/utils/modalUtils.tsx';
import { getPatientDisplayName } from '@/utils/patientUtils.ts';
import {
    applyOptimisticMove,
    generateColumns,
    populateColumnsWithAppointments,
} from '@/utils/roomViewUtils.ts/column.ts';
import { isValidMove, prepareKanbanMove } from '@/utils/roomViewUtils.ts/dragAndDrop.ts';

const logger = getLogger(['page', 'room-view']);

const noop = (): void => {};

const RoomViewPage: FC = () => {
    const [checkinOwners, setCheckinOwners] = useState<Set<symbol>>(() => new Set());
    const [dragging_appointment, setDraggingAppointment] = useState<DailyAppointment | undefined>();

    const releaseCheckinLock = (token: symbol): void => {
        setCheckinOwners((prev) => {
            if (!prev.has(token)) return prev;
            const next = new Set(prev);
            next.delete(token);
            return next;
        });
    };
    const checkInActions = useCheckInActions();
    const { data: enriched_practitioner_data } = useEnrichedPractitionerData();
    const { search_text, selected_rooms, selected_practitioners } = useFilterStore(
        useShallow((state) => ({
            search_text: state.search_text,
            selected_rooms: state.selected_rooms,
            selected_practitioners: state.selected_practitioners,
        }))
    );

    const {
        appointments,
        is_loading,
        mutate: mutateAppointmentsForDate,
    } = useAppointmentsForDate({
        show_cancelled: false,
        show_cancelled_today: true,
    });

    const { check_in_data, mutate: mutateCheckinData, getAppointmentCheckinData } = useCheckInData();

    const { location_key, configs } = useConfig();

    // Get current instance's selected practitioners and rooms
    const current_selected_practitioners = useMemo(() => {
        return selected_practitioners[location_key] ?? [];
    }, [selected_practitioners, location_key]);

    // Filter rooms based on selection, always include waiting rooms
    const initial_columns = useMemo(() => {
        const current_selected_rooms = selected_rooms[location_key] ?? [];
        const rooms: Room[] = [];
        for (const config of configs) {
            const instance_id = config.instance_id;
            const enriched_rooms = (config.rooms ?? []).map((room) => ({
                ...room,
                instance_id: instance_id,
            }));
            rooms.push(...enriched_rooms);
        }

        const filtered_rooms = filterRoomsBySelection(rooms, current_selected_rooms);
        return generateColumns(filtered_rooms);
    }, [configs, location_key, selected_rooms]);

    const [collapsed_map, setCollapsedMap] = useState<Map<string, boolean>>(
        new Map([...initial_columns.keys()].map((key) => [key, false]))
    );
    const [collapse_treatment_room, setCollapseTreatmentRoom] = useState<boolean>(false);

    const openPatientCard = (appointment: DailyAppointment): void => {
        if (!appointment.patient) {
            useWalkInStore.setState({
                selected_instance_id: getInstanceId(appointment),
                selected_appointment: appointment,
            });
            openWalkInModal(
                () => addPatientToAppointment(mutateAppointmentsForDate),
                undefined,
                WalkInModalMode.ADD_PATIENT_TO_APPOINTMENT
            );
            return;
        }

        const eterno_patient_id = getEternoPatientId(appointment);
        const pvs_patient_id = getPvsPatientId(appointment);
        if (!pvs_patient_id) {
            logger.error('No pvs_patient_id found for appointment', { appointment: appointment });
            return;
        }
        const appt_instance_id = appointment.patient?.pvs.instance_id;
        if (!appt_instance_id) {
            logger.error('No instance id found for appointment', { appointment: appointment });
            return;
        }
        openPatientModal(
            pvs_patient_id,
            eterno_patient_id,
            appt_instance_id,
            getPatientDisplayName(appointment) ?? '',
            appointment,
            {
                onClose: async () => {
                    await mutateCheckinData();
                    void mutateAppointmentsForDate();
                },
            }
        );
    };

    const base_filled_columns = useMemo(() => {
        if (!appointments) return undefined;

        const filtered_appointments: DailyAppointment[] = filterAppointments(
            appointments,
            search_text,
            undefined,
            current_selected_practitioners
        );

        return populateColumnsWithAppointments(filtered_appointments, initial_columns, getAppointmentCheckinData);
    }, [appointments, initial_columns, search_text, current_selected_practitioners, getAppointmentCheckinData]);

    const { pendingMoves, inFlightAppointmentIds, registerMove, clearMove } = usePendingMoves();

    let filled_columns = base_filled_columns;
    if (filled_columns && pendingMoves.size > 0) {
        for (const [appointmentId, move] of pendingMoves) {
            filled_columns = applyOptimisticMove(filled_columns, appointmentId, move.columnId);
        }
    }

    // Drop pending moves for server-removed appointments and release their lock; reconcile against unfiltered `appointments` so filtered-out (still-present) ones aren't dropped.
    if (appointments && pendingMoves.size > 0) {
        const presentIds = new Set(appointments.map((appointment) => appointment.id));
        for (const [appointmentId, move] of pendingMoves) {
            if (!presentIds.has(appointmentId)) {
                clearMove(appointmentId, move.token);
                releaseCheckinLock(move.token);
            }
        }
    }

    const isFirstColumnDisabled = checkinOwners.size > 0;

    const toggleCollapse = (id: string): void => {
        setCollapsedMap((prev) => {
            const new_map = new Map(prev);
            new_map.set(id, !prev.get(id));
            return new_map;
        });
    };

    const renderColumn = (
        column_type: ColumnType,
        collapsable: boolean,
        check_in_data: CheckInDataMap,
        match_instance_id: boolean,
        is_column_blocked: boolean = false
    ): ReactElement[] | undefined => {
        if (!filled_columns) {
            return undefined;
        }

        return [...filled_columns.values()]
            .filter((col) => col.type === column_type)
            .map((col) => {
                const is_column_drop_disabled = dragging_appointment
                    ? !isValidMove(col, dragging_appointment, match_instance_id)[0]
                    : false;
                return (
                    <KanbanColumnComponent
                        key={col.id}
                        column={col}
                        collapsed={collapsable ? collapsed_map.get(col.id) : undefined}
                        toggleCollapse={collapsable ? toggleCollapse : undefined}
                        collapsable={collapsable}
                        onAppointmentClick={openPatientCard}
                        onMutateAppointments={mutateAppointmentsForDate}
                        checkInData={check_in_data}
                        isDragDisabled={is_column_blocked}
                        isDropDisabled={is_column_drop_disabled}
                        nonDraggableAppointmentIds={inFlightAppointmentIds}
                    />
                );
            });
    };

    const first_column = renderColumn(ColumnType.EXPECTED, false, check_in_data, false, isFirstColumnDisabled);

    const second_column = (
        <div className="flex flex-col gap-4">{renderColumn(ColumnType.WAITING_ROOM, true, check_in_data, true)}</div>
    );

    const third_column = (
        <div className="flex flex-col gap-4">
            <button
                className="flex w-full justify-between px-4 tracking-wider text-slate-500"
                onClick={() => {
                    setCollapseTreatmentRoom(!collapse_treatment_room);
                }}
            >
                <Paragraph className="text-sm xl:text-base">{'ZIMMER'.toUpperCase()}</Paragraph>
                <ChevronDown
                    className={`text-slate-400 transition-transform ${collapse_treatment_room ? 'rotate-180' : ''}`}
                />
            </button>
            <Collapse expanded={!collapse_treatment_room}>
                {renderColumn(ColumnType.TREATMENT_ROOM, false, check_in_data, true)}
            </Collapse>
            {renderColumn(ColumnType.DISCHARGED, true, check_in_data, false)}
        </div>
    );

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 20, // Drag only starts after moving the mouse distance in px
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 100,
                tolerance: 5,
            },
        })
    );

    const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
        // reset dragging appointment
        setDraggingAppointment(undefined);

        if (!filled_columns) return;

        // basic validation
        const kanban_move_data = prepareKanbanMove(event, filled_columns);
        if (!kanban_move_data) return;
        const { source_column, destination_column, item: appointment } = kanban_move_data;
        if (source_column.id === destination_column.id) return;

        if (isFirstColumnDisabled && source_column.type === ColumnType.EXPECTED) {
            showWarningNotification('Bitte warten Sie, bis der letzte Check-in abgeschlossen ist');
            return;
        }

        if (inFlightAppointmentIds.has(appointment.id)) return;

        const moveToken = registerMove(appointment.id, destination_column.id);

        await checkInActions.dispatchDragMove(
            {
                appointment: appointment,
                sourceColumn: source_column,
                destinationColumn: destination_column,
                checkinData: getAppointmentCheckinData(appointment),
                enrichedPractitionerData: enriched_practitioner_data?.find(
                    (data_item) => data_item.instance_id === getInstanceId(appointment)
                ),
            },
            {
                onSettled: (): void => {
                    clearMove(appointment.id, moveToken);
                    releaseCheckinLock(moveToken);
                },
                onReverted: (): void => {
                    clearMove(appointment.id, moveToken);
                    releaseCheckinLock(moveToken);
                },
                acquireCheckinLock: (): void => {
                    setCheckinOwners((prev) => {
                        const next = new Set(prev);
                        next.add(moveToken);
                        return next;
                    });
                },
            }
        );
    };

    const handleDragStart = (event: DragStartEvent): void => {
        const appointment = [...(filled_columns?.values() || [])]
            .flatMap((col) => col.items)
            .find((app) => app.id === event.active.id);

        setDraggingAppointment(appointment);
    };

    return (
        <Page className="gap-10 overflow-hidden p-4 pb-0 pt-10 xl:p-10 xl:pb-0">
            <PageTitleBar
                title="Termine"
                ctaButton={
                    <Button
                        onClick={() =>
                            openWalkInModal(() => openAppointmentScheduler({ mode: 'book' }), mutateAppointmentsForDate)
                        }
                    >
                        Termin erstellen
                    </Button>
                }
            />
            <div className="w-full">
                {/* TODO: create dedicated reusable component for this page and the list view page */}
                <FilterHeader />
            </div>
            {is_loading ? (
                <Loader />
            ) : (
                <DndContext sensors={sensors} onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
                    <div className="flex min-h-0 w-full flex-shrink flex-grow basis-0 gap-3">
                        <ScrollArea
                            scrollHideDelay={0}
                            classNames={{
                                root: 'h-full basis-2/6',
                                scrollbar: 'opacity-10',
                                viewport: '[&>div]:!block',
                            }}
                        >
                            {first_column}
                        </ScrollArea>
                        <Divider orientation="vertical" />
                        <ScrollArea
                            scrollHideDelay={0}
                            classNames={{
                                root: 'h-full basis-2/6',
                                scrollbar: 'opacity-10',
                                viewport: '[&>div]:!block',
                            }}
                        >
                            {second_column}
                        </ScrollArea>
                        <Divider orientation="vertical" />
                        <ScrollArea
                            scrollHideDelay={0}
                            classNames={{
                                root: 'h-full md:basis-2/6 lg:basis-3/6',
                                scrollbar: 'opacity-10',
                                viewport: '[&>div]:!block',
                            }}
                        >
                            {third_column}
                        </ScrollArea>
                    </div>
                    {/* eslint-disable-next-line unicorn/no-null */}
                    <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={null}>
                        {dragging_appointment && (
                            <div className="opacity-60">
                                <AppointmentCard
                                    appointment={dragging_appointment}
                                    isPending
                                    onClick={noop}
                                    onMutateAppointments={noop}
                                />
                            </div>
                        )}
                    </DragOverlay>
                </DndContext>
            )}
        </Page>
    );
};

export default RoomViewPage;
